import { Feature } from "ol";
import Polyline from 'ol/format/Polyline';
import { Circle, Fill, Stroke, Style, Text } from "ol/style";
import { defaultStyles } from "@/components/Map/DefaultStyles";
import { CarLocation } from "@/components/Passenger/models/RouteWithTrips";
import { Geometry, LineString, Point } from "ol/geom";
import { fromLonLat } from "ol/proj";
import moment from "moment";

interface ICachedRouteStyles {
  whitTimeStamps: Style[] | null;
  withoutTimeStamps: Style[] | null;
}

export default class RouteFeature extends Feature {
  cachedStyles: ICachedRouteStyles = { whitTimeStamps: null, withoutTimeStamps: null };
  timestamp: Date | null = null;

  constructor(route: string | CarLocation[]) {
    let routeGeometry: Geometry;
    const isArray = Array.isArray(route);

    if (isArray) {
      routeGeometry = new LineString(route.map(l => fromLonLat([l.longitude, l.latitude])));
    } else {
      routeGeometry = new Polyline().readGeometry(route, {
        dataProjection: 'EPSG:4326',
        featureProjection: 'EPSG:3857',
      });
    }

    super({ geometry: routeGeometry });

    if (isArray) {
      const styles = this.getLineStringStyle(route);
      this.cachedStyles = {
        whitTimeStamps: styles,
        withoutTimeStamps: styles.filter(s => !(s.getGeometry() instanceof Point)),
      };
      this.setStyle((feature, resolution) => this.getDynamicStyles(resolution));
    } else {
      this.setStyle(this.getPolylineStyle());
    }
  }

  addLocationToRoute(location: CarLocation) {
    const route = this.getGeometry();
    if (route instanceof LineString) {
      route.appendCoordinate(fromLonLat([location.longitude, location.latitude]));
      this.cachedStyles.whitTimeStamps.push(this.getLocationPointStyle(location));
    }
  }

  private getDynamicStyles(resolution: number) {
    return resolution > 10 ? this.cachedStyles.withoutTimeStamps : this.cachedStyles.whitTimeStamps;
  }

  private getPolylineStyle() {
    const colorLine = new Style({
      stroke: new Stroke({
        color: defaultStyles.colors.default,
        width: 6,
        lineDash: [6, 10],
        lineCap: 'round',
      }),
    });

    const baseLine = new Style({
      stroke: new Stroke({
        color: defaultStyles.colors.dark,
        width: 8,
      }),
    });

    return [baseLine, colorLine];
  }

  private getLineStringStyle(route: CarLocation[]) {
    const colorLine = new Style({
      stroke: new Stroke({
        color: defaultStyles.colors.selected,
        width: 4,
      }),
    });

    const baseLine = new Style({
      stroke: new Stroke({
        color: defaultStyles.colors.dark,
        width: 6,
      }),
    });

    const styles = [baseLine, colorLine];
    route.forEach(location => styles.push(this.getLocationPointStyle(location)));
    return styles;
  }

  private getLocationPointStyle(location: CarLocation) {
    const style = new Style({
      geometry: new Point(fromLonLat([location.longitude, location.latitude])),
      image: this.locationCircle,
    });

    if (this.showTimestamp(location.sateliteTime)) {
      style.setText(new Text({
        text: moment(this.timestamp).format('HH:mm'),
        font: defaultStyles.font,
        fill: new Fill({ color: defaultStyles.colors.light }),
        stroke: new Stroke({ color: defaultStyles.colors.dark, width: defaultStyles.strokeWidth }),
        offsetY: -12,
      }));
    }

    return style;
  }

  private showTimestamp(time: Date | string) {
    const dateTime = new Date(time);
    if (!this.timestamp || dateTime.getMinutes() != this.timestamp.getMinutes()) {
      this.timestamp = dateTime;
      return true;
    }
    return false;
  }

  private readonly locationCircle = new Circle({
    fill: new Fill({ color: defaultStyles.colors.dark }),
    stroke: new Stroke({ color: defaultStyles.colors.dark, width: 1 }),
    radius: 4,
  });
}